import React from "react";
import {
  therapeuticIcon,
  diagnosticIcon,
  prognosticIcon,
  fdaIcon,
  oncogenicityIcon,
} from "./oncoKBHelpers";

const therapeuticLevel1 = (
  <div className="tw-flex tw-gap-2 tw-items-center">
    <span>{therapeuticIcon("#33a02c", "30px", 1)}</span>
    <p>
      <b>FDA-recognized</b> biomarker predictive of response to an{" "}
      <b>FDA-approved drug</b> in this indication
    </p>
  </div>
);

const therapeuticLevel2 = (
  <div className="tw-flex tw-gap-2 tw-items-center">
    <span>{therapeuticIcon("#1f78b4", "30px", 2)}</span>
    <p>
      <b>Standard care</b> biomarker recommended by the NCCN or other
      professional guidelines predictive of response to an{" "}
      <b>FDA-approved drug</b> in this indication
    </p>
  </div>
);

const therapeuticLevel3a = (
  <div className="tw-flex tw-gap-2 tw-items-center">
    <span>{therapeuticIcon("#984ea3", "30px", "3A")}</span>
    <p>
      <b>Compelling clinical evidence</b> supports the biomarker as being
      predictive of response to a drug in this indication
    </p>
  </div>
);

const therapeuticLevel3b = (
  <div className="tw-flex tw-gap-2 tw-items-center">
    <span>{therapeuticIcon("#b28cbd", "30px", "3B")}</span>
    <p>
      <b>Standard care</b> or <b>investigational</b> biomarker predictive of
      response to an <b>FDA-approved</b> or <b>investigational</b> drug in
      another indication
    </p>
  </div>
);

const therapeuticLevel4 = (
  <div className="tw-flex tw-gap-2 tw-items-center">
    <span>{therapeuticIcon("#424242", "30px", 4)}</span>
    <p>
      <b>Compelling biological evidence</b> supports the biomarker as being
      predictive of response to a drug
    </p>
  </div>
);

const therapeuticLevelR1 = (
  <div className="tw-flex tw-gap-2 tw-items-center">
    <span>{therapeuticIcon("#ee3424", "30px", "R1")}</span>
    <p>
      <b>Standard care</b> biomarker predictive of <b>resistance</b> to an{" "}
      <b>FDA-approved</b> drug in this indication
    </p>
  </div>
);

const therapeuticLevelR2 = (
  <div className="tw-flex tw-gap-2 tw-items-center">
    <span>{therapeuticIcon("#f79a92", "30px", "R2")}</span>
    <p>
      <b>Compelling clinical evidence</b> supports the biomarker as being
      predictive of <b>resistance</b> to a drug
    </p>
  </div>
);

export const therapeuticLevels = (
  <div>
    <div className="tw-font-bold tw-text-center" style={{ fontSize: "14px" }}>
      Explanation for Symbols of Therapeutic Levels
    </div>
    <br />
    {therapeuticLevel1}
    <br />
    {therapeuticLevel2}
    <br />
    {therapeuticLevel3a}
    <br />
    {therapeuticLevel3b}
    <br />
    {therapeuticLevel4}
    <br />
    {therapeuticLevelR1}
    <br />
    {therapeuticLevelR2}
  </div>
);

export function therapeuticText(therapeuticLevel) {
  switch (therapeuticLevel) {
    case "LEVEL_1":
      return therapeuticLevel1;
    case "LEVEL_2":
      return therapeuticLevel2;
    case "LEVEL_3A":
      return therapeuticLevel3a;
    case "LEVEL_3B":
      return therapeuticLevel3b;
    case "LEVEL_4":
      return therapeuticLevel4;
    case "LEVEL_R1":
      return therapeuticLevelR1;
    case "LEVEL_R2":
      return therapeuticLevelR2;
    default:
      return null;
  }
}

const diagnosticLevel1 = (
  <div className="tw-flex tw-gap-2 tw-items-center">
    <span>{diagnosticIcon("#33a02c", "30px", 1)}</span>
    <p>
      <b>FDA and/or professional guideline-recognized</b> biomarker required for
      diagnosis in this indication
    </p>
  </div>
);

const diagnosticLevel2 = (
  <div className="tw-flex tw-gap-2 tw-items-center">
    <span>{diagnosticIcon("#1f78b4", "30px", 2)}</span>
    <p>
      <b>FDA and/or professional guideline-recognized</b> biomarker that
      supports diagnosis in this indication
    </p>
  </div>
);

const diagnosticLevel3 = (
  <div className="tw-flex tw-gap-2 tw-items-center">
    <span>{diagnosticIcon("#984ea3", "30px", 3)}</span>
    <p>
      Biomarker that <b>may assist disease diagnosis</b> in this indication
      based on <b>clinical evidence</b>
    </p>
  </div>
);

const prognosticLevel1 = (
  <div className="tw-flex tw-gap-2 tw-items-center">
    <span>{prognosticIcon("#33a02c", "30px", 1)}</span>
    <p>
      <b>FDA and/or professional guideline-recognized</b> biomarker prognostic
      in this indication based on <b>a well-powered study (or studies)</b>
    </p>
  </div>
);

const prognosticLevel2 = (
  <div className="tw-flex tw-gap-2 tw-items-center">
    <span>{prognosticIcon("#1f78b4", "30px", 2)}</span>
    <p>
      <b>FDA and/or professional guideline-recognized</b> biomarker prognostic
      in this indication based on <b>a single or multiple small studies</b>
    </p>
  </div>
);

const prognosticLevel3 = (
  <div className="tw-flex tw-gap-2 tw-items-center">
    <span>{prognosticIcon("#984ea3", "30px", 3)}</span>
    <p>
      Biomarker is prognostic in this indication based on{" "}
      <b>clinical evidence</b> in <b>well-powered studies</b>
    </p>
  </div>
);

export function prognosticText(prognosticLevel) {
  switch (prognosticLevel) {
    case "LEVEL_Px1":
      return prognosticLevel1;
    case "LEVEL_Px2":
      return prognosticLevel2;
    case "LEVEL_Px3":
      return prognosticLevel3;
    default:
      return null;
  }
}

export function diagnosticText(diagnosticLevel) {
  switch (diagnosticLevel) {
    case "LEVEL_Dx1":
      return diagnosticLevel1;
    case "LEVEL_Dx2":
      return diagnosticLevel2;
    case "LEVEL_Dx3":
      return diagnosticLevel3;
    default:
      return null;
  }
}

export const diagnosticLevels = (
  <div>
    <div className="tw-font-bold tw-text-center tw-text-sm">
      Explanation for Symbols of Diagnostic Levels
    </div>
    <br />
    {diagnosticLevel1}
    <br />
    {diagnosticLevel2}
    <br />
    {diagnosticLevel3}
  </div>
);

export const prognosticLevels = (
  <div>
    <div className="tw-font-bold tw-text-center tw-text-sm">
      Explanation for Symbols of Prognostic Levels
    </div>
    <br />
    <div className="tw-flex tw-gap-2 tw-items-center">
      <span>{prognosticIcon("#33a02c", "30px", 1)}</span>
      <p>
        <b>FDA and/or professional guideline-recognized</b> biomarker prognostic
        in this indication based on <b>a well-powered study (or studies)</b>
      </p>
    </div>
    <br />
    <div className="tw-flex tw-gap-2 tw-items-center">
      <span>{prognosticIcon("#1f78b4", "30px", 2)}</span>
      <p>
        <b>FDA and/or professional guideline-recognized</b> biomarker prognostic
        in this indication based on <b>a single or multiple small studies</b>
      </p>
    </div>
    <br />
    <div className="tw-flex tw-gap-2 tw-items-center">
      <span>{prognosticIcon("#984ea3", "30px", 3)}</span>
      <p>
        Biomarker is prognostic in this indication based on{" "}
        <b>clinical evidence</b> in <b>well-powered studies</b>
      </p>
    </div>
  </div>
);

export const fdaLevels = (
  <div>
    <div className="tw-font-bold tw-text-center tw-text-sm">
      Explanation for Symbols of FDA Levels
    </div>
    <br />
    <div className="tw-flex tw-gap-2 tw-items-center">
      <span>{fdaIcon("30px", 1)}</span>
      <p>Companion Diagnostics (CDx)</p>
    </div>
    <br />
    <div className="tw-flex tw-gap-2 tw-items-center">
      <span>{fdaIcon("30px", 2)}</span>
      <p>Cancer mutations with evidence of clinical significance</p>
    </div>
    <br />
    <div className="tw-flex tw-gap-2 tw-items-center">
      <span>{fdaIcon("30px", 3)}</span>
      <p>Cancer mutations with potential clinical significance</p>
    </div>
  </div>
);

const oncogenicLevel1a = (
  <div className="tw-flex tw-gap-2 tw-items-center">
    <span>{oncogenicityIcon("oncogenic", "30px")}</span>
    <p>Oncogenic</p>
  </div>
);

const oncogenicLevel1b = (
  <div className="tw-flex tw-gap-2 tw-items-center">
    <span>{oncogenicityIcon("likely oncogenic", "30px")}</span>
    <p>Likely Oncogenic</p>
  </div>
);

const oncogenicLevel1c = (
  <div className="tw-flex tw-gap-2 tw-items-center">
    <span>{oncogenicityIcon("resistance", "30px")}</span>
    <p>Resistance</p>
  </div>
);

const oncogenicLevel2 = (
  <div className="tw-flex tw-gap-2 tw-items-center">
    <span>{oncogenicityIcon("likely neutral", "30px")}</span>
    <p>Likely Neutral</p>
  </div>
);

const oncogenicLevel3 = (
  <div className="tw-flex tw-gap-2 tw-items-center">
    <span>{oncogenicityIcon("inconclusive", "30px")}</span>
    <p>Inconclusive</p>
  </div>
);

const oncogenicLevel4 = (
  <div className="tw-flex tw-gap-2 tw-items-center">
    <span>{oncogenicityIcon("vus", "30px")}</span>
    <p>VUS</p>
  </div>
);

const oncogenicLevel5 = (
  <div className="tw-flex tw-gap-2 tw-items-center">
    <span>{oncogenicityIcon("unknown", "30px")}</span>
    <p>Unknown</p>
  </div>
);

export function oncogenicText(oncogenicity) {
  switch (oncogenicity.toLowerCase()) {
    case "oncogenic":
      return oncogenicLevel1a;
    case "likely oncogenic":
      return oncogenicLevel1b;
    case "resistance":
      return oncogenicLevel1c;
    case "likely neutral":
      return oncogenicLevel2;
    case "inconclusive":
      return oncogenicLevel3;
    case "vus":
      return oncogenicLevel4;
    case "unknown":
      return oncogenicLevel5;
    default:
      return null;
  }
}

export const oncogenicityLevels = (
  <div>
    <div className="tw-font-bold tw-text-center tw-text-sm">
      Explanation for Oncogenicity Symbols
    </div>
    <br />
    <div className="tw-flex tw-gap-2 tw-items-center">
      <span>{oncogenicityIcon("oncogenic", "30px")}</span>
      <p>Oncogenic / Likely Oncogenic / Resistance</p>
    </div>
    <br />
    {oncogenicLevel2}
    <br />
    {oncogenicLevel3}
    <br />
    {oncogenicLevel4}
    <br />
    {oncogenicLevel5}
  </div>
);
